import React, { useState, useMemo, useRef, useEffect } from 'react';
import { PlusIcon, PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Block, PrixReliability } from '../../types';
import { allData as ouvragesData, Ouvrage } from '../../../1-bibliotheque-prix/data/ServicesData';
import { getAllLots, Lot } from '../../../2-ratio/data/lotsData';
import { getAllEnsembles } from '../../data/ensemblesData';
import { Ensemble } from '../../types/EnsembleTypes';
import { computeBenchmarkStats } from '../../../6-challenger-devis/utils/benchmarkStats';
import { fuzzyMatchOuvrages } from '../../../6-challenger-devis/utils/fuzzyMatch';
import { parsePrixString } from '../../../6-challenger-devis/utils/priceParsing';
import { BlockCalculationService, DecompositionParams } from '../../services';
import { FPLabel } from '../atoms';
import VentilationCostRow from './VentilationCostRow';

function sourcesToReliability(count: number): PrixReliability {
  if (count >= 5) return 'high';
  if (count >= 2) return 'medium';
  return 'low';
}

interface VentilationSourceRowProps {
  block: Block;
  costLines: Block[];
  allBlocks: Block[];
  ouvrages: Ouvrage[];
  onBlocksChange: (blocks: Block[]) => void;
  /** Paramètres MO/appro — permettent la décomposition F/MO sur chaque ligne ventilée. */
  decompositionParams?: DecompositionParams;
}

type MatchType = 'prestation' | 'lot' | 'ensemble';

interface SearchResult {
  id: string;
  type: MatchType;
  nom: string;
  description?: string;
  unite?: string;
  data: Ouvrage | Lot | Ensemble;
}

// ─── Type badge ───────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<MatchType, string> = { prestation: 'Prestation', lot: 'Lot', ensemble: 'Ensemble' };
const TYPE_COLORS: Record<MatchType, string> = {
  prestation: 'bg-green-100 text-green-800',
  lot: 'bg-blue-100 text-blue-800',
  ensemble: 'bg-purple-100 text-purple-800',
};
const TYPE_COLORS_ACTIVE: Record<MatchType, string> = {
  prestation: 'bg-green-500 text-white',
  lot: 'bg-blue-500 text-white',
  ensemble: 'bg-purple-500 text-white',
};
const TYPE_DOT: Record<MatchType, string> = {
  prestation: 'bg-green-500',
  lot: 'bg-blue-500',
  ensemble: 'bg-purple-500',
};

// ─── Création de CostLines ───────────────────────────────────────────────────

function makeCostLines(result: SearchResult, parentBlock: Block): Block[] {
  const parentQty = parentBlock.content.quantite ?? '1';
  const parentLevel = (parentBlock.level ?? 0) + 1;
  const baseId = `ventilation-cost-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  if (result.type === 'prestation') {
    const ouvrage = result.data as Ouvrage;
    const stats = computeBenchmarkStats(ouvrage);
    const avg = stats.avg;
    const qty = parseFloat(parentQty.replace(',', '.')) || 1;
    const sources = ouvrage.chantiers
      .map(c => ({
        id: c.id,
        nom: c.nom,
        localisation: c.localisation,
        annee: c.annee,
        fournisseur: c.fournisseur,
        prixUnitaire: parsePrixString(c.prixActualise || c.prixUnitaire),
        active: true,
      }))
      .filter(s => s.prixUnitaire > 0);
    return [{
      id: baseId,
      parentId: parentBlock.id,
      order: 0,
      level: parentLevel,
      children: [],
      content: {
        titre: ouvrage.intitule,
        intitule: ouvrage.intitule,
        unite: ouvrage.unite,
        quantite: parentQty,
        prixUnitaire: avg > 0 ? `${BlockCalculationService.formatPrice(avg)} €` : '0 €',
        prixTotal: BlockCalculationService.formatPrice(avg * qty),
        isVentilationCostLine: true,
        prixUnitaireSource: 'database' as const,
        prixUnitaireMatchType: 'prestation' as const,
        prixUnitaireReliability: sourcesToReliability(sources.length),
        prixUnitaireSources: sources,
      },
    }];
  }

  if (result.type === 'lot') {
    const lot = result.data as Lot;
    return [{
      id: baseId,
      parentId: parentBlock.id,
      order: 0,
      level: parentLevel,
      children: [],
      content: {
        titre: lot.nom,
        intitule: lot.nom,
        unite: lot.unite,
        quantite: parentQty,
        prixUnitaire: '0 €',
        prixTotal: '0',
        isVentilationCostLine: true,
        prixUnitaireSource: 'user' as const, // lot = pas de prix DB → manuel par défaut
      },
    }];
  }

  // ensemble → 1 CostLine par bloc
  // groupe    : toutes les prestations des sections
  // ventilation : les composants (la prestation parente n'est pas une cost line ici)
  const ensemble = result.data as Ensemble;
  const sourceBlocs: Block[] =
    ensemble.mode === 'groupe'
      ? ensemble.sections.flatMap(section => section.blocs)
      : ensemble.composants;
  const lines: Block[] = [];
  let order = 0;
  sourceBlocs.forEach(bloc => {
    // parsePrixString évite le double € si prixUnitaire contient déjà le symbole
    const pu = parsePrixString(bloc.content.prixUnitaire ?? '0');
    const blocQty = parseFloat((bloc.content.quantite ?? '1').replace(',', '.')) || 1;
    lines.push({
      id: `${baseId}-${order}`,
      parentId: parentBlock.id,
      order: order++,
      level: parentLevel,
      children: [],
      content: {
        titre: bloc.content.intitule || bloc.content.titre || '',
        intitule: bloc.content.intitule || bloc.content.titre || '',
        unite: bloc.content.unite ?? '',
        quantite: bloc.content.quantite ?? '1',
        prixUnitaire: `${BlockCalculationService.formatPrice(pu)} €`,
        prixTotal: BlockCalculationService.formatPrice(pu * blocQty),
        isVentilationCostLine: true,
        prixUnitaireSource: 'database' as const,
        prixUnitaireMatchType: 'ensemble' as const,
      },
    });
  });
  return lines;
}

// ─── Dropdown de recherche unifiée ───────────────────────────────────────────

interface AssociationDropdownProps {
  onSelect: (result: SearchResult) => void;
  onFreeEntry: (label: string) => void;
  onClose: () => void;
  sourceLabel?: string;  // intitulé de la ligne source → fuzzy suggestions
  contextBlocks?: Block[]; // tous les blocs → associations passées
}

const SectionHeader: React.FC<{ label: string }> = ({ label }) => (
  <div className="px-3 pt-2 pb-1">
    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{label}</span>
  </div>
);

const AssociationDropdown: React.FC<AssociationDropdownProps> = ({
  onSelect, onFreeEntry, onClose,
  sourceLabel = '',
  contextBlocks = [],
}) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // ── État vide : associations passées ──────────────────────────────────────
  const pastAssociations = useMemo<SearchResult[]>(() => {
    const counts = new Map<string, { type: string; id: string; count: number }>();
    contextBlocks.forEach(b => {
      if (!b.content.matchedItemId || !b.content.matchedItemType) return;
      const key = `${b.content.matchedItemType}-${b.content.matchedItemId}`;
      const ex = counts.get(key);
      if (ex) ex.count++;
      else counts.set(key, { type: b.content.matchedItemType, id: b.content.matchedItemId, count: 1 });
    });

    return Array.from(counts.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 4)
      .flatMap(({ type, id }): SearchResult[] => {
        if (type === 'prestation') {
          const o = ouvragesData.find(o => String(o.id) === id);
          return o ? [{ id: `prestation-${o.id}`, type: 'prestation', nom: o.intitule, description: o.categorie, unite: o.unite, data: o }] : [];
        }
        if (type === 'lot') {
          const l = getAllLots().find(l => l.id === id);
          return l ? [{ id: `lot-${l.id}`, type: 'lot', nom: l.nom, description: l.description ?? '', unite: l.unite, data: l }] : [];
        }
        if (type === 'ensemble') {
          const e = getAllEnsembles().find(e => e.id === id);
          return e ? [{ id: `ensemble-${e.id}`, type: 'ensemble', nom: e.nom, description: e.description ?? '', data: e }] : [];
        }
        return [];
      });
  }, [contextBlocks]);

  // ── État vide : suggestions par similarité ────────────────────────────────
  // Ligne source ventilée → recherche limitée aux ouvrages F+P. L'association
  // source ↔ ouvrage reflète une prestation vendue, pas un composant matière.
  const suggestions = useMemo<SearchResult[]>(() => {
    if (!sourceLabel.trim()) return [];
    const pastIds = new Set(pastAssociations.map(p => p.id));
    return fuzzyMatchOuvrages(sourceLabel, ouvragesData, 'F+P')
      .filter(r => r.score > 0.2)
      .slice(0, 5)
      .map(r => ({
        id: `prestation-${r.ouvrage.id}`,
        type: 'prestation' as MatchType,
        nom: r.ouvrage.intitule,
        description: r.ouvrage.categorie,
        unite: r.ouvrage.unite,
        data: r.ouvrage,
      }))
      .filter(r => !pastIds.has(r.id));
  }, [sourceLabel, pastAssociations]);

  // ── Saisie active : résultats catalogue ───────────────────────────────────
  const catalogResults = useMemo<SearchResult[]>(() => {
    if (query.length < 2) return [];
    const q = query.toLowerCase();
    const out: SearchResult[] = [];
    getAllLots().forEach(lot => {
      if (lot.nom.toLowerCase().includes(q) || lot.description?.toLowerCase().includes(q) || lot.categorie.toLowerCase().includes(q))
        out.push({ id: `lot-${lot.id}`, type: 'lot', nom: lot.nom, description: lot.description, unite: lot.unite, data: lot });
    });
    ouvragesData.forEach(o => {
      if (o.intitule.toLowerCase().includes(q) || o.categorie.toLowerCase().includes(q))
        out.push({ id: `prestation-${o.id}`, type: 'prestation', nom: o.intitule, description: o.categorie, unite: o.unite, data: o });
    });
    getAllEnsembles().forEach(e => {
      if (e.nom.toLowerCase().includes(q) || e.description.toLowerCase().includes(q) || e.categorie.toLowerCase().includes(q) || e.tags.some(t => t.toLowerCase().includes(q)))
        out.push({ id: `ensemble-${e.id}`, type: 'ensemble', nom: e.nom, description: e.description, data: e });
    });
    return out.slice(0, 10);
  }, [query]);

  const isSearching = query.trim().length > 0;
  const navigableResults: SearchResult[] = isSearching
    ? catalogResults
    : [...pastAssociations, ...suggestions];

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => prev < navigableResults.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : navigableResults.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < navigableResults.length) {
          onSelect(navigableResults[selectedIndex]); onClose();
        } else if (query.trim().length > 0) {
          onFreeEntry(query.trim()); onClose();
        }
        break;
      case 'Escape':
        onClose();
        break;
    }
  };

  const renderResult = (result: SearchResult, navIndex: number) => {
    // Badge F / F+P uniquement pour les prestations (ouvrages de biblio).
    // Les lots et ensembles n'ont pas de discriminant F/F+P.
    const ouvrageType =
      result.type === 'prestation' ? (result.data as Ouvrage).type : undefined;
    return (
      <div
        key={result.id}
        className={`cursor-pointer select-none px-3 py-2 ${navIndex === selectedIndex ? 'bg-violet-600 text-white' : 'text-gray-900 hover:bg-gray-50'}`}
        onClick={() => { onSelect(result); onClose(); }}
      >
        <div className="flex items-center gap-2">
          {ouvrageType && <FPLabel ouvrageType={ouvrageType} />}
          <span className="font-medium text-sm truncate">{result.nom}</span>
          <span className={`px-1.5 py-0.5 text-xs rounded-full whitespace-nowrap flex-shrink-0 ${navIndex === selectedIndex ? TYPE_COLORS_ACTIVE[result.type] : TYPE_COLORS[result.type]}`}>
            {TYPE_LABELS[result.type]}
          </span>
        </div>
        <div className={`text-xs mt-0.5 truncate ${navIndex === selectedIndex ? 'text-violet-200' : 'text-gray-500'}`}>
          {result.description}{result.unite ? ` • ${result.unite}` : ''}
        </div>
      </div>
    );
  };

  return (
    <div ref={containerRef} className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-30">
      {/* Input recherche */}
      <div className="p-2 relative">
        <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
          <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setSelectedIndex(-1); }}
          onKeyDown={handleKeyDown}
          placeholder="Rechercher ou saisir librement…"
          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
        />
      </div>

      <div className="max-h-72 overflow-y-auto py-1">
        {/* ── État vide : sections suggestions + passées ── */}
        {!isSearching && (
          <>
            {pastAssociations.length > 0 && (
              <>
                <SectionHeader label="Utilisé précédemment" />
                {pastAssociations.map((r, i) => renderResult(r, i))}
              </>
            )}

            {suggestions.length > 0 && (
              <>
                <div className="border-t border-gray-100 mt-1" />
                <SectionHeader label="Suggestions" />
                {suggestions.map((r, i) => renderResult(r, pastAssociations.length + i))}
              </>
            )}

            {pastAssociations.length === 0 && suggestions.length === 0 && (
              <p className="px-3 py-3 text-xs text-gray-400 text-center">
                Tapez pour rechercher dans le catalogue
              </p>
            )}
          </>
        )}

        {/* ── En saisie : saisie libre + résultats catalogue ── */}
        {isSearching && (
          <>
            <button
              onClick={() => { onFreeEntry(query.trim()); onClose(); }}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <PencilIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <span>Saisie libre : <span className="font-medium">« {query.trim()} »</span></span>
            </button>

            {catalogResults.length > 0 && <div className="border-t border-gray-100 mt-1" />}
            {catalogResults.map((r, i) => renderResult(r, i))}

            {query.length >= 2 && catalogResults.length === 0 && (
              <p className="px-3 py-3 text-xs text-gray-400 text-center">
                Aucun résultat — Entrée pour saisir librement
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ─── Composant principal ─────────────────────────────────────────────────────

const VentilationSourceRow: React.FC<VentilationSourceRowProps> = ({
  block,
  costLines,
  allBlocks,
  ouvrages,
  onBlocksChange,
  decompositionParams,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const matchedItemId   = block.content.matchedItemId;
  const matchedItemType = block.content.matchedItemType;

  // Label du bouton association
  const matchedLabel = useMemo(() => {
    if (!matchedItemId || !matchedItemType) return null;
    if (matchedItemType === 'prestation') return ouvragesData.find(o => String(o.id) === matchedItemId)?.intitule ?? null;
    if (matchedItemType === 'lot')        return getAllLots().find(l => l.id === matchedItemId)?.nom ?? null;
    if (matchedItemType === 'ensemble')   return getAllEnsembles().find(e => e.id === matchedItemId)?.nom ?? null;
    return null;
  }, [matchedItemId, matchedItemType]);

  // Le PU de la ligne source est construit PAR la ventilation — le sync dans
  // VentilationView garantit PU source = total ventilé / qté. Pas de "PU de
  // vente" distinct, donc pas de notion de marge à ce niveau.
  const qty = parseFloat((block.content.quantite ?? '1').replace(',', '.')) || 1;
  const costTotalNum = costLines.reduce((sum, c) => sum + BlockCalculationService.parsePrice(c.content.prixTotal ?? '0'), 0);
  const puVentile    = costLines.length > 0 && qty > 0 ? costTotalNum / qty : null;

  // ─── Handlers "Associer…" (remplace les CostLines existantes) ────────────────

  const handleSelect = (result: SearchResult) => {
    const existingIds = new Set(costLines.map(c => c.id));
    let updated = allBlocks.filter(b => !existingIds.has(b.id));
    updated = updated.map(b =>
      b.id === block.id
        ? { ...b, content: { ...b.content, matchedItemId: String((result.data as any).id), matchedItemType: result.type } }
        : b
    );
    const newLines = makeCostLines(result, block);
    onBlocksChange([...updated, ...newLines]);
  };

  const handleFreeEntry = (label: string) => {
    // Saisie libre : remplace les CostLines existantes par une ligne manuelle
    const existingIds = new Set(costLines.map(c => c.id));
    let updated = allBlocks
      .filter(b => !existingIds.has(b.id))
      .map(b => b.id === block.id
        ? { ...b, content: { ...b.content, matchedItemId: undefined, matchedItemType: undefined } }
        : b
      );
    const newCostLine: Block = {
      id: `ventilation-cost-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      parentId: block.id,
      order: 0,
      level: (block.level ?? 0) + 1,
      children: [],
      content: {
        titre: label,
        intitule: label,
        unite: block.content.unite ?? '',
        quantite: block.content.quantite ?? '1',
        prixUnitaire: '0 €',
        prixTotal: '0',
        isVentilationCostLine: true,
        prixUnitaireSource: 'user' as const, // saisie manuelle
      },
    };
    onBlocksChange([...updated, newCostLine]);
  };

  // Déassocier = retirer le lien ET le PU qui en découlait.
  // Cohérence : le PU d'une ligne ventilée provient de l'association (moyenne
  // benchmark du catalogue). Garder le PU après déassociation laisserait un
  // prix orphelin sans traçabilité. On remet à zéro et on laisse l'utilisateur
  // ressaisir manuellement ou ré-associer.
  const handleClear = () => {
    const existingIds = new Set(costLines.map(c => c.id));
    let updated = allBlocks.filter(b => !existingIds.has(b.id));
    updated = updated.map(b =>
      b.id === block.id
        ? {
            ...b,
            content: {
              ...b.content,
              matchedItemId: undefined,
              matchedItemType: undefined,
              prixUnitaire: '0 €',
              prixTotal: '0',
              prixUnitaireSource: undefined,
              prixUnitaireReliability: undefined,
              prixUnitaireSources: undefined,
              prixUnitaireMatchType: undefined,
              prixUnitaireExpertCurated: undefined,
              prixUnitaireOriginal: undefined,
            },
          }
        : b
    );
    onBlocksChange(updated);
  };

  // ─── "+" : crée une ligne vide, l'association se fait via la barre de recherche ────
  //
  // Ajouter une ligne signale que l'utilisateur veut revoir la composition :
  // on retire donc l'association (matchedItemId/Type) pour ne plus prétendre
  // refléter l'ouvrage biblio d'origine. Seul l'ajout déclenche ce retrait —
  // la simple ouverture du panneau de ventilation laisse l'association intacte.
  const handleAddEmpty = () => {
    const newCostLine: Block = {
      id: `ventilation-cost-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      parentId: block.id,
      order: costLines.length,
      level: (block.level ?? 0) + 1,
      children: [],
      content: {
        titre: '',
        intitule: '',
        unite: block.content.unite ?? '',
        quantite: block.content.quantite ?? '1',
        prixUnitaire: '0 €',
        prixTotal: '0',
        isVentilationCostLine: true,
        prixUnitaireSource: 'user' as const,
      },
    };
    const updated = allBlocks.map(b =>
      b.id === block.id
        ? { ...b, content: { ...b.content, matchedItemId: undefined, matchedItemType: undefined } }
        : b
    );
    onBlocksChange([...updated, newCostLine]);
  };

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      {/* Ligne source */}
      <div className="flex items-center gap-3 px-6 py-3 hover:bg-gray-50/60 transition-colors">
        {/* Label */}
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-gray-800 truncate block">
            {block.content.intitule || block.content.titre || '—'}
          </span>
        </div>

        {/* Qté / Unité */}
        <div className="flex items-center gap-1 text-xs text-gray-500 w-20 flex-shrink-0">
          <span>{block.content.quantite ?? '—'}</span>
          <span>{block.content.unite ?? ''}</span>
        </div>

        {/* Bouton association + croix de dissociation (siblings — nested buttons = HTML invalide) */}
        <div className="relative flex-shrink-0 w-72 flex items-stretch">
          <button
            type="button"
            onClick={() => setShowDropdown(v => !v)}
            className={`flex-1 min-w-0 text-left px-3 py-1.5 text-xs border border-gray-200 hover:border-violet-400 hover:bg-violet-50 transition-colors flex items-center gap-1.5 ${
              matchedLabel && matchedItemType ? 'rounded-l-lg border-r-0' : 'rounded-lg'
            }`}
          >
            {matchedLabel && matchedItemType ? (
              <>
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${TYPE_DOT[matchedItemType]}`} />
                <span className="text-violet-700 font-medium truncate">{matchedLabel}</span>
              </>
            ) : (
              <span className="text-gray-400 italic">Associer…</span>
            )}
          </button>
          {matchedLabel && matchedItemType && (
            <button
              type="button"
              onClick={handleClear}
              title="Dissocier — retire le lien et le prix unitaire associé"
              className="flex-shrink-0 px-1.5 border border-gray-200 rounded-r-lg text-gray-400 hover:text-red-600 hover:bg-red-50 hover:border-red-300 transition-colors"
            >
              <XMarkIcon className="w-3.5 h-3.5" />
            </button>
          )}
          {showDropdown && (
            <AssociationDropdown
              onSelect={handleSelect}
              onFreeEntry={handleFreeEntry}
              onClose={() => setShowDropdown(false)}
              sourceLabel={block.content.intitule || block.content.titre || ''}
              contextBlocks={allBlocks}
            />
          )}
        </div>

        {/* PU calculé issu de la ventilation (total ventilé / qté souhaitée).
            Même style que « PU composé » sur les lignes décomposées : pastille
            bleue + tooltip — signale que le PU est une valeur dérivée (ici de
            la ventilation, pas saisie). */}
        <div className="w-24 text-right flex-shrink-0 tabular-nums">
          {puVentile !== null ? (
            <span className="relative group/puv inline-flex items-center justify-end gap-1 w-full">
              <span className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 bg-blue-500" />
              <span className="text-xs text-violet-700">
                {BlockCalculationService.formatPrice(puVentile)} €
              </span>
              <span className="pointer-events-none absolute bottom-full right-0 mb-2 w-56 opacity-0 group-hover/puv:opacity-100 transition-opacity duration-150 z-50">
                <span className="block bg-gray-900 text-white text-xs rounded-lg px-3 py-2.5 shadow-xl text-left">
                  <span className="flex items-center gap-1 mb-1 text-blue-400 font-semibold">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400" />
                    PU issu de la ventilation
                  </span>
                  <span className="text-gray-400 block">
                    Total ventilé ({BlockCalculationService.formatPrice(costTotalNum)} €) ÷ {qty} {block.content.unite ?? ''}
                  </span>
                </span>
                <span className="absolute top-full right-3 border-4 border-transparent border-t-gray-900" />
              </span>
            </span>
          ) : (
            <span className="text-xs text-gray-300" title="Aucune ligne ventilée — PU calculé indisponible">—</span>
          )}
        </div>

        {/* Total de la ligne = somme des totaux des cost lines (= PU ventilé × qté). */}
        <div className="text-sm font-medium text-gray-700 w-24 text-right flex-shrink-0 tabular-nums">
          {costLines.length > 0
            ? `${BlockCalculationService.formatPrice(costTotalNum)} €`
            : <span className="text-gray-300">—</span>}
        </div>

        {/* Bouton + : crée une ligne vide directement */}
        <button
          onClick={handleAddEmpty}
          title="Ajouter une ligne de coût"
          className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-violet-500 hover:bg-violet-100 transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
        </button>
      </div>

      {/* CostLines enfants */}
      {costLines.map(costLine => (
        <VentilationCostRow
          key={costLine.id}
          block={costLine}
          parentBlock={block}
          allBlocks={allBlocks}
          ouvrages={ouvrages}
          onBlocksChange={onBlocksChange}
          decompositionParams={decompositionParams}
        />
      ))}
    </div>
  );
};

export default VentilationSourceRow;
